// src/lib/map/attribution.js
//
// What a map owes the tiles it shows (REM-684): every MapLibre map in the UI
// credits its sources through this module. PadPlanner's maps had it first
// (5dc98806); it moved here so the others share it.
//
//   Mapbox  the Mapbox logo and the text attribution with working links,
//           "Improve this map" pointing at the place on screen (Mapbox's
//           terms; mapbox-gl draws both, MapLibre neither the logo nor a
//           live link). Satellite styles add Maxar.
//   Esri    the services' own credit (their MapServer copyrightText, read
//           2026-10-30) and "Powered by Esri", on World Imagery's source,
//           which every Esri map here shows.
//
// The text is MapLibre's own AttributionControl, fed by each source's
// attribution and expanded (attributionControl: { compact: false }).
// MapLibre hides it when no credited source is drawn. bindAttribution keeps
// the Improve link on the camera and collapses the text to MapLibre's "i"
// button on a map under 480 px wide: MapLibre's own threshold, 640 px, would
// collapse narrow maps that have room for one line.
//
// The logo is mapbox-gl 3.9.4's own (dist/mapbox-gl.css, a.mapboxgl-ctrl-logo),
// copied as published, not redrawn. A map with a credits footer
// (components/map/MapCreditsFooter.jsx, S-e slice 2: PadPlannerMap) has the
// logo and the credits there, MapLibre's control off; any other map adds
// MapboxLogoControl (attachAttribution does), bottom left.
//
//   Copernicus  "Contains modified Copernicus Sentinel data YYYY" on the
//           Sentinel-2 reference source (B4), shown while that lane is drawn.
//
// AI PRIME's own imagery (/tiles/) is credited nowhere yet: its credit needs
// its text first (REM-684).

export const COMPACT_BELOW_PX = 480;
export const FEEDBACK_URL = "https://apps.mapbox.com/feedback/";

/** mapbox-gl's "Improve this map" link for a style and a camera, as mapbox-gl builds it. */
export function feedbackHref({ styleId, lng, lat, zoom }) {
  return `${FEEDBACK_URL}?owner=mapbox&id=${encodeURIComponent(styleId)}#/${lng.toFixed(5)}/${lat.toFixed(5)}/${(Math.round(zoom * 100) / 100).toFixed(2)}`;
}

/**
 * Keep a map's attribution current: the Improve link on the camera, and the
 * compact form below COMPACT_BELOW_PX. styleId() names the Mapbox style shown
 * now, or is null on a map with no Mapbox tiles; root() is where the control is, which is not the map's container once
 * PadPlannerMap has moved the corners into its HUD. Returns the unbind.
 */
export function bindAttribution(map, { styleId, root = () => map.getContainer?.() }) {
  const update = () => {
    const container = map.getContainer?.();
    const el = root()?.querySelector(".maplibregl-ctrl-attrib");
    if (!el) return;
    const id = styleId?.();
    if (id) {
      const c = map.getCenter();
      const href = feedbackHref({ styleId: id, lng: c.lng, lat: c.lat, zoom: map.getZoom() });
      for (const a of el.querySelectorAll(`a[href^="${FEEDBACK_URL}"]`)) if (a.getAttribute("href") !== href) a.setAttribute("href", href);
    }
    const compact = container.clientWidth > 0 && container.clientWidth < COMPACT_BELOW_PX;
    if (compact !== el.classList.contains("maplibregl-compact")) {
      el.classList.toggle("maplibregl-compact", compact);
      el.classList.remove("maplibregl-compact-show");
    }
  };
  // MapLibre rewrites the text when a source or the style changes; those
  // events come after its own handlers, so the link is set again after.
  const EVENTS = ["load", "moveend", "resize", "styledata", "sourcedata"];
  for (const e of EVENTS) map.on(e, update);
  update();
  return () => { for (const e of EVENTS) map.off(e, update); };
}

export const MAPBOX_LOGO_URI =
  "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' fill-rule='evenodd' viewBox='0 0 88 23'%3E%3Cdefs%3E%3Cpath id='logo' d='M11.5 2.25c5.105 0 9.25 4.145 9.25 9.25s-4.145 9.25-9.25 9.25-9.25-4.145-9.25-9.25 4.145-9.25 9.25-9.25zM6.997 15.983c-.051-.338-.828-5.802 2.233-8.873a4.395 4.395 0 013.13-1.28c1.27 0 2.49.51 3.39 1.42.91.9 1.42 2.12 1.42 3.39 0 1.18-.449 2.301-1.28 3.13C12.72 16.93 7 16 7 16l-.003-.017zM15.3 10.5l-2 .8-.8 2-.8-2-2-.8 2-.8.8-2 .8 2 2 .8z'/%3E%3Cpath id='text' d='M50.63 8c.13 0 .23.1.23.23V9c.7-.76 1.7-1.18 2.73-1.18 2.17 0 3.95 1.85 3.95 4.17s-1.77 4.19-3.94 4.19c-1.04 0-2.03-.43-2.74-1.18v3.77c0 .13-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V8.23c0-.12.1-.23.23-.23h1.4zm-3.86.01c.01 0 .01 0 .01-.01.13 0 .22.1.22.22v7.55c0 .12-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V15c-.7.76-1.69 1.19-2.73 1.19-2.17 0-3.94-1.87-3.94-4.19 0-2.32 1.77-4.19 3.94-4.19 1.03 0 2.02.43 2.73 1.18v-.75c0-.12.1-.23.23-.23h1.4zm26.375-.19a4.24 4.24 0 00-4.16 3.29c-.13.59-.13 1.19 0 1.77a4.233 4.233 0 004.17 3.3c2.35 0 4.26-1.87 4.26-4.19 0-2.32-1.9-4.17-4.27-4.17zM60.63 5c.13 0 .23.1.23.23v3.76c.7-.76 1.7-1.18 2.73-1.18 1.88 0 3.45 1.4 3.84 3.28.13.59.13 1.2 0 1.8-.39 1.88-1.96 3.29-3.84 3.29-1.03 0-2.02-.43-2.73-1.18v.77c0 .12-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V5.23c0-.12.1-.23.23-.23h1.4zm-34 11h-1.4c-.13 0-.23-.11-.23-.23V8.22c.01-.13.1-.22.23-.22h1.4c.13 0 .22.11.23.22v.68c.5-.68 1.3-1.09 2.16-1.1h.03c1.09 0 2.09.6 2.6 1.55.45-.95 1.4-1.55 2.44-1.56 1.62 0 2.93 1.25 2.9 2.78l.03 5.2c0 .13-.1.23-.23.23h-1.41c-.13 0-.23-.11-.23-.23v-4.59c0-.98-.74-1.71-1.62-1.71-.8 0-1.46.7-1.59 1.62l.01 4.68c0 .13-.11.23-.23.23h-1.41c-.13 0-.23-.11-.23-.23v-4.59c0-.98-.74-1.71-1.62-1.71-.85 0-1.54.79-1.6 1.8v4.5c0 .13-.1.23-.23.23zm53.615 0h-1.61c-.04 0-.08-.01-.12-.03-.09-.06-.13-.19-.06-.28l2.43-3.71-2.39-3.65a.213.213 0 01-.03-.12c0-.12.09-.21.21-.21h1.61c.13 0 .24.06.3.17l1.41 2.37 1.4-2.37a.34.34 0 01.3-.17h1.6c.04 0 .08.01.12.03.09.06.13.19.06.28l-2.37 3.65 2.43 3.7c0 .05.01.09.01.13 0 .12-.09.21-.21.21h-1.61c-.13 0-.24-.06-.3-.17l-1.44-2.42-1.44 2.42a.34.34 0 01-.3.17zm-7.12-1.49c-1.33 0-2.42-1.12-2.42-2.51 0-1.39 1.08-2.52 2.42-2.52 1.33 0 2.42 1.12 2.42 2.51 0 1.39-1.08 2.51-2.42 2.52zm-19.865 0c-1.32 0-2.39-1.11-2.42-2.48v-.07c.02-1.38 1.09-2.49 2.4-2.49 1.32 0 2.41 1.12 2.41 2.51 0 1.39-1.07 2.52-2.39 2.53zm-8.11-2.48c-.01 1.37-1.09 2.47-2.41 2.47s-2.42-1.12-2.42-2.51c0-1.39 1.08-2.52 2.4-2.52 1.33 0 2.39 1.11 2.41 2.48l.02.08zm18.12 2.47c-1.32 0-2.39-1.11-2.41-2.48v-.06c.02-1.38 1.09-2.48 2.41-2.48s2.42 1.12 2.42 2.51c0 1.39-1.09 2.51-2.42 2.51z'/%3E%3C/defs%3E%3Cmask id='clip'%3E%3Crect x='0' y='0' width='100%25' height='100%25' fill='white'/%3E%3Cuse xlink:href='%23logo'/%3E%3Cuse xlink:href='%23text'/%3E%3C/mask%3E%3Cg id='outline' opacity='0.3' stroke='%23000' stroke-width='3'%3E%3Ccircle mask='url(%23clip)' cx='11.5' cy='11.5' r='9.25'/%3E%3Cuse xlink:href='%23text' mask='url(%23clip)'/%3E%3C/g%3E%3Cg id='fill' opacity='0.9' fill='%23fff'%3E%3Cuse xlink:href='%23logo'/%3E%3Cuse xlink:href='%23text'/%3E%3C/g%3E%3C/svg%3E";

const link = (href, text) => `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;

export const MAPBOX_ATTRIBUTION =
  `${link("https://www.mapbox.com/about/maps/", "© Mapbox")} ${link("https://www.openstreetmap.org/copyright/", "© OpenStreetMap")} ` +
  link(FEEDBACK_URL, "<strong>Improve this map</strong>");

// The satellite styles carry Maxar's imagery, and Mapbox's satellite TileJSON
// credits it; the others are Mapbox and OpenStreetMap only.
const MAXAR_CREDIT = ` ${link("https://www.maxar.com/", "© Maxar")}`;

/** The attribution for a Mapbox style id: the satellite styles add Maxar. */
export function basemapAttribution(styleId) {
  return styleId.startsWith("satellite") ? MAPBOX_ATTRIBUTION + MAXAR_CREDIT : MAPBOX_ATTRIBUTION;
}

/**
 * The Mapbox Satellite tileset (v4 mapbox.satellite), which some maps draw
 * directly: credited as the satellite style, whose id its Improve link names.
 */
export const SATELLITE_STYLE_ID = "satellite-v9";

export const POWERED_BY_ESRI = link("https://www.esri.com/", "Powered by Esri");

/**
 * Each Esri service's credit, its MapServer copyrightText as published.
 * "Powered by Esri" rides on World Imagery's, the one every Esri map shows.
 * MapLibre drops a credit contained in another, so Transportation's is shown
 * through Boundaries and Places' where both are drawn.
 */
export const ESRI_CREDITS = Object.freeze({
  World_Imagery: `${POWERED_BY_ESRI} · Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community`,
  "Reference/World_Transportation": "Esri, HERE, Garmin, (c) OpenStreetMap contributors",
  "Reference/World_Boundaries_and_Places": "Esri, HERE, Garmin, (c) OpenStreetMap contributors, and the GIS user community",
});

/** The credit for an Esri tile URL (services.arcgisonline.com/ArcGIS/rest/services/<service>/MapServer/...), or null. */
export function esriAttribution(tileUrl) {
  const m = /services\.arcgisonline\.com\/ArcGIS\/rest\/services\/(.+?)\/MapServer\//.exec(tileUrl ?? "");
  return (m && ESRI_CREDITS[m[1]]) ?? null;
}

/**
 * The credit Copernicus asks of modified Sentinel data, for the year of the
 * composite (an acquisition_date, YYYY_MM_DD, or a year). It goes on the
 * Sentinel-2 reference raster source, so the attribution control shows it
 * while the reference lane is on the map, and in the reference badge (B4).
 */
export function copernicusAttribution(date) {
  return `Contains modified Copernicus Sentinel data ${String(date ?? "").slice(0, 4)}`;
}

/** Mapbox's logo as a MapLibre control (bottom left), for a map outside PadPlannerMap's HUD. */
export class MapboxLogoControl {
  onAdd() {
    const box = document.createElement("div");
    box.className = "maplibregl-ctrl";
    const a = document.createElement("a");
    a.className = "aip-mapbox-logo";
    a.href = "https://www.mapbox.com/";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.setAttribute("aria-label", "Mapbox");
    a.dataset.testid = "mapbox-logo";
    Object.assign(a.style, {
      display: "block", width: "88px", height: "23px", backgroundImage: `url("${MAPBOX_LOGO_URI}")`,
      backgroundRepeat: "no-repeat", backgroundSize: "contain", pointerEvents: "auto",
    });
    box.append(a);
    this._box = box;
    return box;
  }

  onRemove() { this._box?.remove(); this._box = null; }
}

/**
 * The Mapbox style a tile URL draws: styles/v1/mapbox/<id>/tiles (the Static
 * Tiles API, basemapTileUrl's), or the satellite style for the v4
 * mapbox.satellite tileset. Null for any other URL.
 */
export function mapboxStyleIdOf(tileUrl) {
  const u = tileUrl ?? "";
  const m = /api\.mapbox\.com\/styles\/v1\/mapbox\/([^/]+)\/tiles\//.exec(u);
  if (m) return m[1];
  return /api\.mapbox\.com\/v4\/mapbox\.satellite\//.test(u) ? SATELLITE_STYLE_ID : null;
}

/**
 * Credit a map built with attributionControl: { compact: false }. With
 * mapboxSource (the id of its Mapbox raster source): Mapbox's logo, the live
 * Improve link, and the source's credit kept on the style its tiles now
 * draw, so a basemap switched by setTiles (MapView's picker, purchase mode)
 * names its own style and adds or drops Maxar. Every map: the compact form
 * under COMPACT_BELOW_PX. Returns the detach.
 */
export function attachAttribution(map, { mapboxSource = null } = {}) {
  const source = () => (mapboxSource ? map.getSource?.(mapboxSource) : null);
  const styleId = () => mapboxStyleIdOf(source()?.tiles?.[0]);
  // MapLibre's control reads source.attribution on the next sourcedata.
  const recredit = () => {
    const src = source(), id = styleId();
    if (src && id && src.attribution !== basemapAttribution(id)) src.attribution = basemapAttribution(id);
  };
  const logo = mapboxSource ? new MapboxLogoControl() : null;
  if (logo) map.addControl(logo, "bottom-left");
  if (mapboxSource) map.on("sourcedata", recredit);
  const unbind = bindAttribution(map, { styleId: mapboxSource ? styleId : null });
  return () => {
    unbind();
    if (mapboxSource) map.off("sourcedata", recredit);
    if (logo) { try { map.removeControl(logo); } catch { /* the map is already gone */ } }
  };
}
